// Core Modules
import { Route, Routes } from '@angular/router';

// Guards
import { AuthGuard } from '@core/guards/auth/auth.guard';
import { PermissionGuard } from '@core/guards/permission/permission.guard';
import { SessionValidationGuard } from '@core/guards/session-validation/session-validation.guard';

// Resolvers
import { FederatedDomainResolver } from '@core/resolvers/federated-domain.resolver';

// Components
import { AuthLoginComponent } from '@shared/components/auth-login/auth-login.component';
import { NoAccessComponent } from '@shared/components/no-access/no-access.component';
import { UnauthorizedComponent } from '@shared/components/unauthorized/unauthorized.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

// Navigation
import { NAV_AREAS } from '@layout/side-nav/nav.model';

/**
 * Build a lazily-loaded, permission-gated feature-area route.
 * @param path route base (from MainNavigationEnum)
 * @param loadChildren dynamic import of the area's routes file
 * @param permission app-permission that gates the area
 * @returns the configured Route
 */
const featureRoutes = (path: string, loadChildren: Route['loadChildren'], permission: string): Route => ({
  path,
  loadChildren,
  canActivate: [SessionValidationGuard, PermissionGuard],
  data: { permissions: { only: [permission], redirectTo: MainNavigationEnum.UNAUTHORIZED } },
});

export const routes: Routes = [
  {
    path: MainNavigationEnum.LOGIN,
    component: AuthLoginComponent,
    canActivate: [AuthGuard],
    resolve: { data: FederatedDomainResolver },
    title: 'Login',
  },

  // Feature areas, derived from the nav registry so the rail, the router and the landing page
  // cannot disagree. Add a screen in NAV_AREAS, not here.
  ...NAV_AREAS.map((area) => featureRoutes(area.path, area.loadChildren, area.permission)),

  {
    path: MainNavigationEnum.UNAUTHORIZED,
    component: UnauthorizedComponent,
    pathMatch: 'full',
    canActivate: [SessionValidationGuard],
    title: 'Unauthorized',
  },
  {
    path: MainNavigationEnum.NO_ACCESS,
    component: NoAccessComponent,
    pathMatch: 'full',
    canActivate: [SessionValidationGuard],
    title: 'No access',
  },
  // Redirect to /login if match is ""
  { path: '', redirectTo: `/${MainNavigationEnum.LOGIN}`, pathMatch: 'full' },
  // Redirect to /login if no route match is found
  { path: '**', redirectTo: `/${MainNavigationEnum.LOGIN}` },
];
